import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  getCards,
  addCard,
  deleteCard,
  setDefaultCard,
} from "../../../../services/stripe_services";
import { getApiErrorMessage } from "../../../../utils/getApiErrorMessage";
import {
  formatCardBrand,
  formatCardExpiry,
  getCardId,
  isNonEmptyString,
  isUnprovisionedCardsResponse,
  normalizeCardList,
} from "../../../../utils/billingSelection";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "14px",
      fontFamily: "inherit",
      color: "#101828",
      "::placeholder": { color: "#98A2B3" },
    },
    invalid: { color: "#F04438" },
  },
  hidePostalCode: true,
};

const PaymentMethod = ({
  selectedCardId,
  onSelect,
  onContinue,
  onBack,
  title = "Payment Method",
  subtitle = "Select a saved card or add a new one.",
  continueLabel = "Continue",
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const user = useSelector((state) => state?.user);

  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  // Prefilled from the account, but the user can correct them - these go to
  // Stripe as the card's billing details.
  const accountName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();
  const [holderName, setHolderName] = useState(accountName);
  const [billingEmail, setBillingEmail] = useState(user?.email ?? "");
  const [addingCard, setAddingCard] = useState(false);
  const [cardError, setCardError] = useState(null);
  const [deletingCardId, setDeletingCardId] = useState(null);
  const [defaultingCardId, setDefaultingCardId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  /** Keep parent wizard state aligned with the live card list from Stripe. */
  const syncSelectionWithCards = (list) => {
    const safeList = Array.isArray(list)
      ? list.filter((card) => getCardId(card))
      : [];

    if (safeList.length === 0) {
      if (selectedCardId) {
        onSelect?.(null, null);
      }
      return;
    }

    const selectedStillValid =
      isNonEmptyString(String(selectedCardId ?? "")) &&
      safeList.some((card) => getCardId(card) === String(selectedCardId));

    if (!selectedStillValid) {
      onSelect?.(getCardId(safeList[0]), safeList[0]);
    }
  };

  const hasActiveSelection =
    isNonEmptyString(String(selectedCardId ?? "")) &&
    cards.some((card) => getCardId(card) === String(selectedCardId));

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      setLoadingCards(true);
      const data = await getCards();

      if (data?.success) {
        const list = normalizeCardList(data);
        setCards(list);
        syncSelectionWithCards(list);
        return;
      }

      if (isUnprovisionedCardsResponse(data)) {
        setCards([]);
        syncSelectionWithCards([]);
        return;
      }

      toast.error(getApiErrorMessage(data, "Failed to load saved cards."));
    } catch {
      toast.error("Failed to load saved cards.");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!isNonEmptyString(String(cardId ?? ""))) {
      return;
    }

    try {
      setDeletingCardId(cardId);
      const data = await deleteCard(cardId);
      if (data?.success) {
        toast.success("Card removed.");
        setConfirmDeleteId(null);
        await fetchCards();
      } else {
        toast.error(getApiErrorMessage(data, "Failed to remove card."));
      }
    } catch {
      toast.error("Failed to remove card.");
    } finally {
      setDeletingCardId(null);
    }
  };

  const handleMakeDefault = async (cardId) => {
    try {
      setDefaultingCardId(cardId);
      const data = await setDefaultCard(cardId);

      if (data?.success) {
        toast.success("Default card updated. Renewals will use this card.");
        await fetchCards();
      } else {
        toast.error(getApiErrorMessage(data, "Failed to set default card."));
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setDefaultingCardId(null);
    }
  };

  const handleAddCard = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    const cardEl = elements.getElement(CardElement);
    if (!cardEl) {
      setCardError("Card form is not ready. Please try again.");
      return;
    }

    setCardError(null);
    setAddingCard(true);

    try {
      const name = holderName.trim() || accountName;
      const email = billingEmail.trim() || (user?.email ?? "");

      if (!name) {
        setCardError("Full name is required.");
        setAddingCard(false);
        return;
      }

      if (!email) {
        setCardError("Email is required.");
        setAddingCard(false);
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardEl,
        billing_details: { name, email },
      });

      if (error) {
        setCardError(error?.message || "Unable to read card details.");
        setAddingCard(false);
        return;
      }

      if (!paymentMethod?.id) {
        setCardError("Unable to create payment method. Please try again.");
        setAddingCard(false);
        return;
      }

      const data = await addCard({ payment_method_id: paymentMethod.id });

      if (data?.success) {
        const isFirstCard = cards.length === 0;
        const hasDefault = cards.some((card) => card?.is_default);

        // Renewals charge the customer's default card, so a first card (or one
        // added when nothing is default) has to become the default or the next
        // invoice has nothing to charge.
        if (isFirstCard || !hasDefault) {
          await setDefaultCard(paymentMethod.id);
        }

        toast.success("Card added successfully!");
        cardEl.clear();
        setHolderName(accountName);
        setBillingEmail(user?.email ?? "");
        setShowAddForm(false);
        await fetchCards();
      } else {
        setCardError(getApiErrorMessage(data, "Failed to add card."));
      }
    } catch {
      setCardError("Something went wrong. Please try again.");
    } finally {
      setAddingCard(false);
    }
  };

  return (
    <div>
      <h6 className="fw-semibold mb-1" style={{ color: "#101828" }}>
        {title}
      </h6>
      <p className="small text-muted mb-4">{subtitle}</p>

      {/* Saved Cards */}
      {loadingCards ? (
        <div className="text-center py-4">
          <div
            className="spinner-border spinner-border-sm"
            style={{ color: "#7F56D9" }}
            role="status"
          />
          <p className="mt-2 small text-muted">Loading cards...</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-3 mb-4">
          {cards.length === 0 && !showAddForm && (
            <div
              className="rounded-3 p-3 text-center small text-muted"
              style={{ border: "1px dashed #D0D5DD", background: "#FAFAFA" }}
            >
              No saved cards yet. Add a new card below.
            </div>
          )}

          {cards.map((card) => {
            const cardId = getCardId(card);
            if (!cardId) {
              return null;
            }
            const isSelected = String(selectedCardId) === cardId;
            return (
              <div
                key={cardId}
                className="d-flex align-items-center gap-3 rounded-3 px-4 py-3"
                style={{
                  border: isSelected
                    ? "2px solid #7F56D9"
                    : "2px solid #E4E7EC",
                  background: isSelected ? "#FAF5FF" : "#FFFFFF",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  boxShadow: isSelected
                    ? "0 0 0 3px rgba(127,86,217,0.10)"
                    : "0 1px 2px rgba(16,24,40,0.05)",
                }}
                onClick={() => onSelect?.(cardId, card)}
              >
                {/* Radio */}
                <div
                  className="rounded-circle flex-shrink-0"
                  style={{
                    width: 18,
                    height: 18,
                    border: isSelected
                      ? "5px solid #7F56D9"
                      : "2px solid #D0D5DD",
                    background: "#FFFFFF",
                    transition: "all 0.2s",
                  }}
                />

                {/* Icon */}
                <span style={{ fontSize: "1.4rem" }}>💳</span>

                {/* Details */}
                <div className="flex-grow-1">
                  <div className="fw-medium small" style={{ color: "#344054" }}>
                    {formatCardBrand(card?.brand)} •••• {card?.last4 ?? "****"}
                  </div>
                  <div className="small text-muted">
                    Expires {formatCardExpiry(card)}
                  </div>
                </div>

                <div className="d-flex align-items-center gap-2 ms-auto flex-shrink-0">
                  {card?.is_default ? (
                    <span
                      className="badge rounded-pill px-2 py-1"
                      style={{
                        background: "#F4EBFF",
                        color: "#6941C6",
                        fontSize: "0.7rem",
                      }}
                    >
                      Default
                    </span>
                  ) : (
                    <button
                      className="btn btn-sm p-0 fw-medium"
                      style={{
                        color: "#7F56D9",
                        background: "none",
                        border: "none",
                        fontSize: "0.75rem",
                      }}
                      disabled={defaultingCardId === cardId}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleMakeDefault(cardId);
                      }}
                    >
                      {defaultingCardId === cardId
                        ? "Setting..."
                        : "Make default"}
                    </button>
                  )}
                  <button
                    className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
                    style={{
                      width: 28,
                      height: 28,
                      border: "1px solid #FECDCA",
                      background:
                        deletingCardId === cardId ? "#FEF3F2" : "#FFF",
                      color: "#F04438",
                      flexShrink: 0,
                    }}
                    disabled={deletingCardId === cardId}
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDeleteId(cardId);
                    }}
                    title="Remove card"
                  >
                    {deletingCardId === cardId ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        style={{ width: 12, height: 12, borderWidth: 2 }}
                        role="status"
                      />
                    ) : (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4h6v2" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add New Card toggle */}
      {!showAddForm ? (
        <button
          className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
          style={{
            border: "1.5px dashed #7F56D9",
            color: "#7F56D9",
            background: "transparent",
            fontSize: "0.82rem",
            fontWeight: 500,
          }}
          onClick={() => setShowAddForm(true)}
        >
          <span style={{ fontSize: "1rem" }}>＋</span> Add new card
        </button>
      ) : (
        <div
          className="rounded-3 p-4 mt-2"
          style={{
            border: "2px solid #7F56D9",
            background: "#FAF5FF",
          }}
        >
          <div className="fw-medium small mb-3" style={{ color: "#344054" }}>
            New Card Details
          </div>

          <form onSubmit={handleAddCard}>
            <div className="mb-3">
              <label className="form-label small mb-1" style={{ color: "#344054" }}>
                Full name <span style={{ color: "#D92D20" }}>*</span>
              </label>
              <input
                type="text"
                className="form-control"
                required
                value={holderName}
                onChange={(event) => setHolderName(event.target.value)}
                placeholder="Name on the card"
                style={{ border: "1px solid #D0D5DD" }}
              />
            </div>

            <div className="mb-3">
              <label className="form-label small mb-1" style={{ color: "#344054" }}>
                Email <span style={{ color: "#D92D20" }}>*</span>
              </label>
              <input
                type="email"
                className="form-control"
                required
                value={billingEmail}
                onChange={(event) => setBillingEmail(event.target.value)}
                placeholder="Billing email"
                style={{ border: "1px solid #D0D5DD" }}
              />
            </div>

            <div
              className="rounded-3 px-3 py-3 mb-3"
              style={{
                border: "1px solid #D0D5DD",
                background: "#FFFFFF",
              }}
            >
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>

            {cardError && <p className="small text-danger mb-3">{cardError}</p>}

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-sm fw-medium rounded-pill d-flex align-items-center justify-content-center"
                style={{
                  background: addingCard ? "#9E77ED" : "#7F56D9",
                  color: "#FFFFFF",
                  border: "none",
                  minWidth: 110,
                  height: 34,
                  opacity: 1,
                  cursor: addingCard ? "wait" : "pointer",
                  transition: "background 0.2s",
                }}
                disabled={addingCard || !stripe}
              >
                {addingCard ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      style={{ width: 13, height: 13, borderWidth: 2 }}
                      role="status"
                    />
                    Adding...
                  </>
                ) : (
                  "Add Card"
                )}
              </button>
              <button
                type="button"
                className="btn btn-sm px-4 rounded-pill"
                style={{
                  border: "1px solid #D0D5DD",
                  color: "#344054",
                  background: "#FFFFFF",
                }}
                onClick={() => {
                  setShowAddForm(false);
                  setCardError(null);
                }}
                disabled={addingCard}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Actions */}
      <div
        className="d-flex justify-content-between align-items-center mt-4 pt-3"
        style={{ borderTop: "1px solid #F2F4F7" }}
      >
        <button
          className="btn btn-sm px-4 py-2 rounded-pill"
          style={{
            border: "1px solid #D0D5DD",
            color: "#344054",
            background: "#FFFFFF",
          }}
          onClick={onBack}
        >
          ← Back
        </button>
        <button
          className="btn px-5 py-2 fw-semibold rounded-pill"
          style={{
            background: hasActiveSelection ? "#7F56D9" : "#D0D5DD",
            color: "#FFFFFF",
            border: "none",
            minWidth: 180,
          }}
          disabled={!hasActiveSelection || loadingCards}
          onClick={() => {
            if (!hasActiveSelection) {
              return;
            }
            onContinue?.();
          }}
        >
          {continueLabel}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDeleteId &&
        (() => {
          const cardToDelete = cards.find(
            (c) => getCardId(c) === String(confirmDeleteId),
          );
          const brand =
            cardToDelete?.brand || cardToDelete?.card?.brand || "Card";
          const last4 =
            cardToDelete?.last4 || cardToDelete?.card?.last4 || "****";
          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(16,24,40,0.45)",
                zIndex: 1055,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "16px",
              }}
              onClick={() => {
                if (!deletingCardId) setConfirmDeleteId(null);
              }}
            >
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: "28px 24px 24px",
                  maxWidth: 360,
                  width: "100%",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.16)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Icon */}
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "#FEF3F2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#F04438"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                    <path d="M9 6V4h6v2" />
                  </svg>
                </div>
                {/* Title */}
                <h6
                  style={{
                    textAlign: "center",
                    fontWeight: 700,
                    color: "#101828",
                    marginBottom: 8,
                    fontSize: "1rem",
                  }}
                >
                  Remove Card
                </h6>
                {/* Message */}
                <p
                  style={{
                    textAlign: "center",
                    color: "#667085",
                    fontSize: "0.875rem",
                    marginBottom: 24,
                  }}
                >
                  Are you sure you want to remove your{" "}
                  <strong
                    style={{ color: "#344054", textTransform: "capitalize" }}
                  >
                    {brand}
                  </strong>{" "}
                  card ending in{" "}
                  <strong style={{ color: "#344054" }}>•••• {last4}</strong>?
                  This action cannot be undone.
                </p>
                {/* Buttons */}
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    className="btn btn-sm rounded-pill"
                    style={{
                      flex: 1,
                      border: "1px solid #D0D5DD",
                      color: "#344054",
                      background: "#fff",
                      fontWeight: 500,
                      padding: "8px 0",
                    }}
                    disabled={!!deletingCardId}
                    onClick={() => setConfirmDeleteId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm rounded-pill"
                    style={{
                      flex: 1,
                      background: "#F04438",
                      color: "#fff",
                      border: "none",
                      fontWeight: 600,
                      padding: "8px 0",
                    }}
                    disabled={!!deletingCardId}
                    onClick={() => handleDeleteCard(confirmDeleteId)}
                  >
                    {deletingCardId === confirmDeleteId ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        style={{ width: 14, height: 14, borderWidth: 2 }}
                        role="status"
                      />
                    ) : (
                      "Remove Card"
                    )}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default PaymentMethod;
